import { ErrorBoundary } from '@sentry/nextjs';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { queryCache, useQuery } from 'react-query';

import Flex from 'shopper/components/Flex';
import Spinner from 'shopper/components/Spinner';

import ComparaiProductsList from 'components/ComparaiProductsList';
import GenericError from 'components/GenericError';
import Layout from 'components/Layout';
import WishlistSuggestions from 'components/Wishlist/WishlistSuggestions';

import useSendPageView from 'hooks/useSendPageView';

import { sendEvent } from 'lib/gtag';
import { getOfferPageEventCategory, isValidApprovedOffer } from 'lib/offer';
import { commentQueryKeys, offerQueryKeys } from 'lib/queryKeys';
import { get } from 'lib/request';
import { sendSupixEvent } from 'lib/supix';

import { useUser } from 'providers/UserProvider';
import { useUtm } from 'providers/UtmProvider';

import OfferBreadcrumb from './Breadcrumb';
import DynamicallyLoadedOfferCommentsWithRef from './DynamicallyLoadedOfferCommentsWithRef';
import OfferAdditionalInfos from './OfferAdditionalInfos';
import OfferHead from './OfferHead';
import OfferMain from './OfferMain';
import OfferSchema from './OfferSchema';
import OfferSidebar from './Sidebar';

import COMMENT from 'constants/comment';
import COMPARAI from 'constants/comparai';
import STATUS_CODES from 'constants/statusCodes';

const OfferReviewList = dynamic(() => import('./OfferReviewList'), {
  ssr: false,
});

const toMappedComparaiProducts = (products) => {
  const { product, prices } = products;

  return prices.map((price) => ({
    product,
    prices: [price],
  }));
};

const COMPARAI_SUBTITLE =
  'Essa oferta foi encerrada, mas confira a seleção de preços que encontramos com nossos parceiros!';
const COMMENTS_FALLBACK_MESSAGE =
  'Ops! Ocorreu um erro inesperado e não possível mostrar os comentários dessa oferta.';

const OfferPage = ({ offer, offerKey }) => {
  const [commentsSort, setCommentsSort] = useState(COMMENT.SORT_VALUES.OLDEST);

  const commentsRef = useRef(null);
  const offerWishlistRef = useRef(null);
  const goToStoreButtonRef = useRef(null);
  const router = useRouter();
  const { loadingLoggedInUser } = useUser();
  const { isUtmLoaded } = useUtm();
  // Most offers comes directly from server for SEO purposes.
  // There are edge cases though where some offers with certain statuses
  // may need the authentication token to be fetched. These needs to be made
  // on client, after the current user is fully loaded.
  // In these cases we don't care about SEO
  const { data: clientOffer } = useQuery(offerQueryKeys.offer({ offerKey }), {
    queryFn: () => get(`/offer/${offerKey}`),
    enabled: !offer && !loadingLoggedInUser,
    // Currently the only status specific offer that the user should be able
    // to access is `WAITING`, and only if the user that posted the offer is
    // the same as the user who is accessing it, otherwise we redirect
    onError: ({ message }) => {
      // For some reason someone tried to access an `WAITING` offer and is
      // also not logged in
      if (message === STATUS_CODES[401]) {
        router.push('/');
      }
    },
  });

  const currOffer = offer || clientOffer;

  useEffect(() => {
    // Cleanup offer-specific unnecessary query caches on route change
    const handleRouteChange = () => {
      queryCache.removeQueries(
        offerQueryKeys.isLiked({ offerId: currOffer?.offerId })
      );
      queryCache.removeQueries(
        commentQueryKeys.offer.isSubscribedForNotifications({
          offerId: currOffer?.offerId,
        })
      );
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    if (currOffer && isUtmLoaded) {
      const { categoryId, offerId, offerStatus, offerStatusName } = currOffer;

      sendSupixEvent('/ofr', {
        oid: offerId,
        status: offerStatus,
        category: categoryId,
      });

      sendEvent({
        action: 'appear',
        category: getOfferPageEventCategory(offerStatusName),
      });
    }
  }, [currOffer, isUtmLoaded]);

  useSendPageView(currOffer?.offerId, 'offer', {
    enabled: !!currOffer?.offerId,
  });

  if (!currOffer) {
    return (
      <Flex alignItems="center" justifyContent="center" flexGrow={1} mt={350}>
        <Spinner />
      </Flex>
    );
  }

  const isShowingComparaiProductsList =
    !isValidApprovedOffer(currOffer.offerStatusName) &&
    currOffer.related?.comparai?.prices.length > 0;

  return (
    <>
      <OfferHead {...currOffer} />
      {/* Only renders schemas if offer comes directly from server, if the
      offer isn't rendered on server as HTML is shouldn't have schemas */}
      {offer && <OfferSchema {...currOffer} />}
      <Layout
        headingComponent={<OfferBreadcrumb {...currOffer} />}
        sidebarComponent={<OfferSidebar offer={currOffer} />}
        footerProps={{ hydrationOnDemand: true }}
        maxWidthOnMobileDevices
        gridProps={{ mb: 10 }}
      >
        {isShowingComparaiProductsList && (
          <ComparaiProductsList
            expandable
            subtitle={COMPARAI_SUBTITLE}
            gaEventCategory={getOfferPageEventCategory(
              currOffer.offerStatusName
            )}
            products={toMappedComparaiProducts(currOffer.related.comparai)}
            productSlug={currOffer.offerSlug}
            utmMedium={COMPARAI.UTM_MEDIUM.FINISHED_OFFER}
            partnerMessageOnBottom
          />
        )}
        <OfferMain
          mt={isShowingComparaiProductsList ? 4 : null}
          commentsElem={commentsRef.current}
          gaEventCategory={getOfferPageEventCategory(currOffer.offerStatusName)}
          goToStoreButtonRef={goToStoreButtonRef}
          offerWishlistElem={offerWishlistRef.current}
          {...currOffer}
        />
        <OfferAdditionalInfos
          gaEventCategory={getOfferPageEventCategory(currOffer.offerStatusName)}
          {...currOffer}
        />
        {currOffer?.offerKeywords.length > 0 && (
          <WishlistSuggestions
            as="section"
            ref={offerWishlistRef}
            keywordsDisplay={currOffer.offerKeywords}
            keywordButtonTypeCanChange
            gaEventCategory={getOfferPageEventCategory(
              currOffer.offerStatusName
            )}
            headingProps={{
              as: 'h2',
              size: 'size4',
              mb: 2,
            }}
            full
          />
        )}
        {currOffer.reviews?.length > 0 && (
          <OfferReviewList
            commentsSort={commentsSort}
            offerKey={offerKey}
            onCommentsSortChange={setCommentsSort}
            {...currOffer}
          />
        )}
        <ErrorBoundary
          fallback={<GenericError message={COMMENTS_FALLBACK_MESSAGE} />}
        >
          <DynamicallyLoadedOfferCommentsWithRef
            ref={commentsRef}
            offer={currOffer}
            offerKey={offerKey}
            commentsSort={commentsSort}
            onCommentsSortChange={setCommentsSort}
          />
        </ErrorBoundary>
      </Layout>
    </>
  );
};

OfferPage.defaultProps = {
  offer: null,
};

OfferPage.propTypes = {
  offer: PropTypes.shape({
    categoryName: PropTypes.string.isRequired,
    categorySlug: PropTypes.string.isRequired,
    hasPhysicalStore: PropTypes.bool.isRequired,
    isOfferCommentsAllowed: PropTypes.bool.isRequired,
    isOfferPriority: PropTypes.bool.isRequired,
    isStoreCnpjValid: PropTypes.bool.isRequired,
    isStoreVerified: PropTypes.bool.isRequired,
    marketplaceRate: PropTypes.bool,
    mostRelevantKeyword: PropTypes.string,
    offerClicks: PropTypes.number.isRequired,
    offerCommentsCount: PropTypes.number.isRequired,
    offerCoupon: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    offerFaq: PropTypes.array.isRequired,
    offerId: PropTypes.number.isRequired,
    offerInstructions: PropTypes.string.isRequired,
    offerInstructionsRaw: PropTypes.string.isRequired,
    offerIsHighlight: PropTypes.bool.isRequired,
    offerKeywords: PropTypes.arrayOf(PropTypes.string).isRequired,
    offerLikes: PropTypes.number.isRequired,
    offerOldPrice: PropTypes.number,
    offerPhoto: PropTypes.string.isRequired,
    offerPrice: PropTypes.number.isRequired,
    offerPriceType: PropTypes.string.isRequired,
    offerPublished: PropTypes.string.isRequired,
    offerRelatedOffers: PropTypes.arrayOf(
      PropTypes.shape({
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerLikes: PropTypes.number.isRequired,
        offerOldPrice: PropTypes.number.isRequired,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerTitle: PropTypes.string.isRequired,
      })
    ).isRequired,
    offerSlug: PropTypes.string.isRequired,
    offerStatusName: PropTypes.string.isRequired,
    offerTags: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      })
    ).isRequired,
    offerTitle: PropTypes.string.isRequired,
    offerType: PropTypes.string.isRequired,
    offerUserDescription: PropTypes.string.isRequired,
    relatedSubcategories: PropTypes.arrayOf(
      PropTypes.shape({
        subcategoryId: PropTypes.number.isRequired,
        subcategoryName: PropTypes.string,
        subcategorySlug: PropTypes.string.isRequired,
        subcategoryWishlist: PropTypes.string.isRequired,
      })
    ).isRequired,
    reviews: PropTypes.array.isRequired,
    storeCouponsCount: PropTypes.number.isRequired,
    storeDescription: PropTypes.string.isRequired,
    storeDomain: PropTypes.string.isRequired,
    storeImage: PropTypes.string.isRequired,
    storeMarketplace: PropTypes.shape({
      hasPhysicalStore: PropTypes.bool.isRequired,
      isMarketplaceCnpjValid: PropTypes.bool.isRequired,
      isMarketplaceVerified: PropTypes.bool.isRequired,
      storeMarketplaceName: PropTypes.string.isRequired,
    }),
    storeName: PropTypes.string.isRequired,
    storeParam: PropTypes.string.isRequired,
    subcategoryName: PropTypes.string,
    subcategorySlug: PropTypes.string.isRequired,
    userCommentsCount: PropTypes.number.isRequired,
    userCouponsCount: PropTypes.number.isRequired,
    userFirstOffer: PropTypes.bool.isRequired,
    userId: PropTypes.number.isRequired,
    userName: PropTypes.string.isRequired,
    userOffersCount: PropTypes.number.isRequired,
    userPhoto: PropTypes.string.isRequired,
    userSpecialtyName: PropTypes.string,
    userUsername: PropTypes.string.isRequired,
  }),
  // `offerKey` is either `offerId` or an `offerSlug`
  offerKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

export default OfferPage;
